import { useState } from 'react';

import { useCartCookie } from '@/helpers/useCartCookie';
import { useCartIsOpen } from '@/app/cart/useCartIsOpen';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import Loader from '@/components/common/Loader';
import Error from '@/components/common/Error';
import { ScrollArea } from '@/components/ui/scroll-area';

import OfferingSessions from './components/OfferingSessions';

import { useActivitiesV2 } from '@/data/activity';
import {
  useOfferingSessions,
  useOfferingSessionStructure
} from '@/data/offeringSession';
import { useAddItemsToCart, usePostCart } from '@/data/cart';

const ViewOfferingSessionsDialog = ({ offeringId, onClose }) => {
  const activities = useActivitiesV2();
  const offeringSessions = useOfferingSessions(offeringId);
  const offeringSessionStructure = useOfferingSessionStructure(offeringId);
  const addItemsToCart = useAddItemsToCart();
  const [selectedOfferingSessions, setSelectedOfferingSessions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const postCart = usePostCart();
  const { setCartId, getCartId } = useCartCookie();
  const cartId = getCartId();
  const { openCart } = useCartIsOpen();

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      if (!cartId) {
        const newCart = await postCart({
          offeringSessionIds: selectedOfferingSessions
        });
        setCartId(newCart.id);
      } else {
        await addItemsToCart(cartId, {
          items: selectedOfferingSessions.map((offeringSessionId) => ({
            type: 'Offering Session',
            quantity: 1,
            offeringSessionId
          }))
        });
      }
      openCart();
      onClose();
    } catch (error) {
      setApiError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const loadingComponent = (
    <div className="h-[175px]">
      <Loader />
    </div>
  );

  let content = (
    <OfferingSessions
      offeringId={offeringId}
      selectedOfferingSessions={selectedOfferingSessions}
      setSelectedOfferingSessions={setSelectedOfferingSessions}
    />
  );

  if (
    (activities.isLoading && !activities.data) ||
    (offeringSessions.isLoading && !offeringSessions.data) ||
    (offeringSessionStructure.isLoading && !offeringSessionStructure.data)
  ) {
    content = loadingComponent;
  }
  if (activities.error) {
    content = <Error error="There was an error loading the activities" />;
  }
  if (offeringSessions.error) {
    content = <Error error="There was an error loading the offeringSessions" />;
  }
  if (offeringSessionStructure.error) {
    content = (
      <Error error="There was an error loading the offeringSessionStructure" />
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] px-2 rounded-lg">
        <DialogHeader className="mb-4">
          <DialogTitle>Offering Availabilities</DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-[500px]">
          <div className="p-2">{content}</div>
        </ScrollArea>
        {apiError && <Error error={apiError} />}
        <DialogFooter>
          <Button
            onClick={handleSubmit}
            disabled={selectedOfferingSessions.length === 0 || isSubmitting}
          >
            Add{' '}
            {selectedOfferingSessions.length === 0
              ? 'item to Cart'
              : selectedOfferingSessions.length === 1
                ? '(1) item to Cart'
                : `(${selectedOfferingSessions.length}) items to Cart`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ViewOfferingSessionsDialog;
